import React, { useEffect } from "react";
import { Form } from "formsy-react";
import makeStyles from "@material-ui/styles/makeStyles";
import { SelectableAttribute } from "./selectable-attribute";
import { ColorAttribute } from "./color-attribute";
import { PrintedTextAttribute } from "./printed-text-attribute";
import { PrintedImageAttribute } from "./printed-image-attribute";
import { IAttribute } from "constants/product-customizations/customization";
import {
    CustomizationSelectionType,
    TCustomizationSelection,
} from "constants/product-customizations/customization-selection.types";
import { IProduct } from "../../../records/item/item";
import { useCustomizationModalContext } from "../context/customization-modal-context";
import { assertExhaustiveSwitch } from "components/utils/validations";
import { themeOptions } from "styles/theme";

import { CustomizationDescription } from "./customization-description";

interface IProps {
    attribute: IAttribute;
    product: IProduct;
    onChange: (value: TCustomizationSelection) => void;
    readonly: boolean;
    companyMerchantId: string;
    onValidationError: (id: string, error: string | null) => void;
    selectedValue: TCustomizationSelection;
}

export const AttributeDetails: React.FC<IProps> = ({
    attribute,
    product,
    selectedValue: selected,
    onChange,
    onValidationError,
    companyMerchantId,
    readonly,
}) => {
    const classes = useStyles();
    const { handleAttributeIdChange } = useCustomizationModalContext();
    const {
        _id: id,
        name,
        isRequired,
        binding,
        selectable,
        color,
        printed,
        childAttributes,
        description,
    } = attribute ?? {};
    const commonProps = <T,>(selected: T) => ({
        selected,
        attributeId: id,
        label: name,
        isRequired,
        target: binding,
        onChange,
        readonly,
    });

    useEffect(
        () => handleAttributeIdChange(id),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [id],
    );

    if (!selected) {
        return null;
    }

    let attributeContainer: JSX.Element | null = null;
    const { types, text, image } = printed ?? {};

    const printedProps = <T,>(selected: T, types: string[]) => ({ types, onValidationError, ...commonProps(selected) });

    switch (selected.type) {
        case CustomizationSelectionType.SELECTABLE:
            if (selectable) {
                attributeContainer = <SelectableAttribute attributeValue={selectable} {...commonProps(selected)} />;
            }

            break;

        case CustomizationSelectionType.COLOR_PICKER:
        case CustomizationSelectionType.COLOR_SWATCH:
            if (color) {
                attributeContainer = (
                    <ColorAttribute
                        picker={color.picker}
                        swatch={color.swatch}
                        onValidationError={onValidationError}
                        {...commonProps(selected)}
                    />
                );
            }

            break;

        case CustomizationSelectionType.PRINTED_TEXT:
            if (text && types) {
                attributeContainer = (
                    <PrintedTextAttribute
                        text={text}
                        childAttributes={childAttributes}
                        {...printedProps(selected, types)}
                    />
                );
            }

            break;

        case CustomizationSelectionType.PRINTED_IMAGE:
            if (image && types) {
                attributeContainer = (
                    <PrintedImageAttribute
                        product={product}
                        image={image}
                        childAttributes={childAttributes}
                        companyMerchantId={companyMerchantId}
                        attribute={attribute}
                        {...printedProps(selected, types)}
                    />
                );
            }

            break;

        default:
            assertExhaustiveSwitch(selected);
    }

    return (
        <div>
            <CustomizationDescription className={classes.attributeDetailsDescription}>
                {description}
            </CustomizationDescription>

            <Form className={classes.attributeDetailsControl}>{attributeContainer}</Form>
        </div>
    );
};

const useStyles = makeStyles({
    attributeDetailsDescription: {
        padding: "10px 20px",
        fontSize: 14,
        color: `${themeOptions.palette.textColor}`,
    },
    attributeDetailsControl: {
        padding: "10px 20px",
    },
});
